import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { PRODUCTS, PRODUCT_DETAILS, RESEARCH, inquiryMailto } from "../data/content";
import { PageShell, ShellSection } from "./PageShell";
import { StatusBadge, PlaceholderFrame } from "../components/common";
import { Reveal } from "../components/Reveal";
import { usePageMeta } from "../hooks/usePageMeta";

export default function ProductPage() {
    const { slug } = useParams();
    const product = PRODUCTS.find((p) => p.slug === slug);
    const detail = product ? PRODUCT_DETAILS[product.slug] : null;
    usePageMeta(
        product ? `${product.name} — Titan Haven Group LLC` : "Portfolio — Titan Haven Group LLC",
        detail ? detail.intro : product?.summary,
        { image: product?.image },
    );
    if (!product) return <Navigate to="/portfolio" replace />;

    const slots = detail?.gallerySlots ?? 1;
    const images = detail?.gallery ?? [];
    const cta = detail?.cta;

    return (
        <PageShell
            testId={`product-page-${product.slug}`}
            overline={`${product.division}${product.category ? " · " + product.category : ""}`}
            title={product.name}
            lede={detail ? detail.intro : product.summary}
        >
            <ShellSection>
                <Reveal className="flex flex-wrap items-center gap-3">
                    <StatusBadge testId="product-page-status">{product.status}</StatusBadge>
                    <span className="text-xs tracking-[0.15em] uppercase text-thg-tertiary">{product.relationship}</span>
                    {product.aka && (
                        <span className="text-xs tracking-[0.15em] uppercase text-thg-tertiary">Also: {product.aka}</span>
                    )}
                </Reveal>
                {(detail?.positioning || detail?.relationshipNote) && (
                    <Reveal delay={0.05} className="mt-8 max-w-3xl" data-testid="product-positioning">
                        {detail?.positioning && (
                            <p className="font-display text-xl md:text-2xl font-medium text-white uppercase tracking-wide">
                                {detail.positioning}
                            </p>
                        )}
                        {(detail?.positioningNote || detail?.relationshipNote) && (
                            <p className="mt-4 text-sm md:text-base leading-relaxed text-thg-secondary">
                                {detail.positioningNote || detail.relationshipNote}
                            </p>
                        )}
                    </Reveal>
                )}
                <Reveal delay={0.1} className="mt-10">
                    {/* Separated, labeled panels — never a gap-px collage */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="product-gallery">
                        {Array.from({ length: slots }).map((_, i) =>
                            images[i] ? (
                                <figure key={i} className={images[i].wide ? "md:col-span-2" : ""}>
                                    <div className="border border-thg-border bg-[#0a0a0a]">
                                        <img
                                            src={images[i].src}
                                            alt={images[i].alt}
                                            className="w-full h-auto"
                                            loading="lazy"
                                        />
                                    </div>
                                    {images[i].caption && (
                                        <figcaption className="mt-3 text-xs tracking-[0.15em] uppercase text-thg-tertiary">
                                            {images[i].caption}
                                        </figcaption>
                                    )}
                                </figure>
                            ) : (
                                <div key={i} className="bg-[#0a0a0a] border border-thg-border">
                                    {/* PLACEHOLDER — authentic screenshot to be supplied by operator */}
                                    <PlaceholderFrame label={i === 0 ? product.name : `${product.name} — view ${i + 1}`} />
                                </div>
                            ),
                        )}
                    </div>
                    {detail?.galleryNote && (
                        <p className="mt-4 text-xs leading-relaxed text-thg-tertiary max-w-2xl">{detail.galleryNote}</p>
                    )}
                </Reveal>
            </ShellSection>
            {detail?.sections?.map((s, i) => (
                <ShellSection key={s.heading}>
                    <Reveal>
                        <h2 className="font-display text-2xl md:text-3xl font-medium text-white">{s.heading}</h2>
                        {s.text && (
                            <p className="mt-4 text-sm md:text-base leading-relaxed text-thg-secondary max-w-3xl">{s.text}</p>
                        )}
                    </Reveal>
                    {s.modules && (
                        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-thg-border">
                            {s.modules.map((m, j) => (
                                <Reveal key={m} delay={j * 0.04} className="border-b border-r border-thg-border p-5 flex items-center gap-3">
                                    <span className="block w-1.5 h-1.5 bg-thg-red shrink-0" aria-hidden="true" />
                                    <span className="text-sm text-thg-secondary">{m}</span>
                                </Reveal>
                            ))}
                        </div>
                    )}
                    {s.link && (
                        <Link
                            to={s.link.to}
                            className="mt-5 inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase text-thg-secondary hover:text-white transition-colors duration-300"
                        >
                            {s.link.label} <ArrowRight size={13} />
                        </Link>
                    )}
                </ShellSection>
            ))}
            {/* Where StoreCentral fits — POS boundary comparison */}
            {detail?.fit && (
                <ShellSection>
                    <Reveal>
                        <h2 className="font-display text-2xl md:text-3xl font-medium text-white">Where StoreCentral fits</h2>
                        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-thg-border border border-thg-border" data-testid="fit-comparison">
                            {[detail.fit.left, detail.fit.right].map((col) => (
                                <div key={col.title} className="bg-thg-surface p-8">
                                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-thg-tertiary">{col.title}</p>
                                    <ul className="mt-5 space-y-3">
                                        {col.items.map((item) => (
                                            <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-thg-secondary">
                                                <span className="mt-2 block w-1.5 h-1.5 bg-thg-red shrink-0" aria-hidden="true" />
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                        <p className="mt-6 text-sm leading-relaxed text-thg-secondary max-w-3xl">{detail.fit.note}</p>
                    </Reveal>
                </ShellSection>
            )}
            {/* StoreCentral or Control Shift? — locked architecture distinction */}
            {detail?.choice && (
                <ShellSection>
                    <Reveal>
                        <h2 className="font-display text-2xl md:text-3xl font-medium text-white">{detail.choice.heading}</h2>
                        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-thg-border border border-thg-border" data-testid="choice-comparison">
                            {[detail.choice.left, detail.choice.right].map((col) => (
                                <div key={col.title} className="bg-thg-surface p-8">
                                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-thg-tertiary">{col.title}</p>
                                    <p className="mt-4 text-sm leading-relaxed text-thg-secondary">{col.text}</p>
                                    {col.link && (
                                        <Link
                                            to={col.link.to}
                                            className="mt-4 inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase text-thg-secondary hover:text-white transition-colors duration-300"
                                        >
                                            {col.link.label} <ArrowRight size={13} />
                                        </Link>
                                    )}
                                </div>
                            ))}
                        </div>
                        <p className="mt-6 text-xs leading-relaxed text-thg-tertiary max-w-3xl">{detail.choice.note}</p>
                    </Reveal>
                </ShellSection>
            )}
            {/* Public pricing — adopted figures; proposals control if they differ */}
            {detail?.pricing && (
                <ShellSection>
                    <Reveal>
                        <h2 className="font-display text-2xl md:text-3xl font-medium text-white">{detail.pricing.heading}</h2>
                        {detail.pricing.lede && (
                            <p className="mt-6 text-sm md:text-base leading-relaxed text-thg-secondary max-w-3xl">{detail.pricing.lede}</p>
                        )}
                        {detail.pricing.cards && (
                        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-thg-border border border-thg-border max-w-3xl" data-testid="product-pricing">
                            {detail.pricing.cards.map((c) => (
                                <div key={c.name} className="bg-thg-surface p-8">
                                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-thg-tertiary">{c.name}</p>
                                    <p className="mt-3 font-display text-4xl font-medium text-white">
                                        {c.price}{" "}
                                        <span className="text-sm text-thg-tertiary font-sans">{c.unit}</span>
                                    </p>
                                    {c.note && <p className="mt-2 text-sm text-thg-secondary">{c.note}</p>}
                                </div>
                            ))}
                        </div>
                        )}
                        {detail.pricing.included && (
                            <p className="mt-6 text-sm md:text-base leading-relaxed text-thg-secondary max-w-3xl">
                                {detail.pricing.included}
                            </p>
                        )}
                        {detail.pricing.rows && (
                            <ul className="mt-6 space-y-3 max-w-3xl">
                                {detail.pricing.rows.map((r) => (
                                    <li key={r} className="flex items-start gap-3 text-sm leading-relaxed text-thg-secondary">
                                        <span className="mt-2 block w-1.5 h-1.5 bg-thg-red shrink-0" aria-hidden="true" />
                                        {r}
                                    </li>
                                ))}
                            </ul>
                        )}
                        {detail.pricing.ctas && (
                            <div className="mt-8 flex flex-wrap gap-4">
                                {detail.pricing.ctas.map((c, i) => (
                                    <a
                                        key={c.label}
                                        href={inquiryMailto(c.subject)}
                                        data-testid={`product-pricing-cta-${i}`}
                                        className={
                                            i === 0
                                                ? "inline-flex items-center gap-2 bg-thg-red text-white hover:bg-thg-redhover transition-colors duration-300 px-6 py-3 text-sm tracking-wide uppercase"
                                                : "inline-flex items-center gap-2 border border-white/40 text-white hover:bg-white hover:text-black transition-colors duration-300 px-6 py-3 text-sm tracking-wide uppercase"
                                        }
                                    >
                                        {c.label} <ArrowUpRight size={15} />
                                    </a>
                                ))}
                            </div>
                        )}
                        {detail.pricing.note && (
                            <p className="mt-6 text-xs leading-relaxed text-thg-tertiary max-w-3xl">{detail.pricing.note}</p>
                        )}
                    </Reveal>
                </ShellSection>
            )}
            {detail?.partners && (
                <ShellSection>
                    <Reveal className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
                        <div className="max-w-2xl">
                            <h2 className="font-display text-2xl md:text-3xl font-medium text-white">
                                For retail technology partners
                            </h2>
                            <p className="mt-4 text-sm md:text-base leading-relaxed text-thg-secondary">{detail.partners.text}</p>
                        </div>
                        <a
                            href={inquiryMailto(detail.partners.cta.subject)}
                            data-testid="partner-fit-cta"
                            className="inline-flex items-center gap-2 border border-white/40 text-white hover:bg-white hover:text-black transition-colors duration-300 px-6 py-3 text-sm tracking-wide uppercase shrink-0"
                        >
                            {detail.partners.cta.label} <ArrowUpRight size={16} />
                        </a>
                    </Reveal>
                </ShellSection>
            )}
            {/* Research foundation — restrained; links resolve from RESEARCH data */}
            {detail?.research && (
                <ShellSection>
                    <Reveal>
                        <p className="text-xs tracking-[0.25em] uppercase font-bold text-thg-tertiary">
                            Research foundation
                        </p>
                        <p className="mt-4 text-sm md:text-base leading-relaxed text-thg-secondary max-w-3xl">
                            {detail.research.text}
                        </p>
                        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2" data-testid="research-foundation-links">
                            {detail.research.topics.map((topic) => {
                                const article = RESEARCH.find((r) => r.topic === topic);
                                return (
                                    <a
                                        key={topic}
                                        href={article?.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase text-thg-secondary hover:text-white transition-colors duration-300"
                                    >
                                        {article?.title || topic} <ArrowUpRight size={12} />
                                    </a>
                                );
                            })}
                        </div>
                    </Reveal>
                </ShellSection>
            )}
            <ShellSection>
                <Reveal className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-4">
                    {detail?.ctaExternal && (
                        <a
                            href={detail.ctaExternal.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="product-primary-cta"
                            className="inline-flex items-center gap-2 bg-thg-red text-white hover:bg-thg-redhover transition-colors duration-300 px-6 py-3 text-sm tracking-wide uppercase"
                        >
                            {detail.ctaExternal.label} <ArrowUpRight size={16} />
                        </a>
                    )}
                    {detail?.ctaSecondary && (
                        <a
                            href={`mailto:${detail.ctaSecondary.email}?subject=${encodeURIComponent(detail.ctaSecondary.subject)}`}
                            data-testid="product-secondary-cta"
                            className="inline-flex items-center gap-2 border border-white/40 text-white hover:bg-white hover:text-black transition-colors duration-300 px-6 py-3 text-sm tracking-wide uppercase"
                        >
                            {detail.ctaSecondary.label} <ArrowUpRight size={16} />
                        </a>
                    )}
                    {cta && (
                        <a
                            href={inquiryMailto(cta.subject)}
                            data-testid="product-inquiry-cta"
                            className="inline-flex items-center gap-2 bg-thg-red text-white hover:bg-thg-redhover transition-colors duration-300 px-6 py-3 text-sm tracking-wide uppercase"
                        >
                            {cta.label} <ArrowUpRight size={16} />
                        </a>
                    )}
                    {product.externalUrl && (
                        <a
                            href={product.externalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="product-live-site-link"
                            aria-label={`${product.name} product website — opens in a new tab`}
                            className="inline-flex items-center gap-2 border border-white/40 text-white hover:bg-white hover:text-black transition-colors duration-300 px-6 py-3 text-sm tracking-wide"
                        >
                            {product.externalUrl.replace(/^https?:\/\//, "")} <ArrowUpRight size={16} />
                        </a>
                    )}
                    <Link
                        to="/portfolio"
                        data-testid="product-back-link"
                        className="inline-flex items-center gap-2 text-sm text-thg-secondary hover:text-white transition-colors duration-300"
                    >
                        <ArrowLeft size={15} /> Back to portfolio
                    </Link>
                </Reveal>
            </ShellSection>
        </PageShell>
    );
}
